import React, { useState, useEffect } from 'react';
import { Lock, ArrowRight, LogOut } from 'lucide-react';
import Dashboard from './components/Dashboard';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');  // For multi-user login
  const [userRole, setUserRole] = useState('admin');  // 'admin' or 'trader'
  const [currentUser, setCurrentUser] = useState('');  // Logged in username
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  // Session watchdog. The auth cookie expires after 24h; in a long-lived tab
  // every panel's background fetch then fails quietly and the dashboard
  // freezes at its last snapshot with no explanation. Re-verify every 5 min
  // and on window focus; only a real HTTP rejection (401) trips the banner —
  // a network blip must not log anyone out.
  useEffect(() => {
    if (!isAuthenticated) {
      setSessionExpired(false);
      return undefined;
    }
    const check = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/auth/verify`,
          { credentials: 'include' }
        );
        if (!response.ok) setSessionExpired(true);
      } catch {
        /* network error — not an auth verdict, keep the session */
      }
    };
    const timer = setInterval(check, 5 * 60 * 1000);
    window.addEventListener('focus', check);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', check);
    };
  }, [isAuthenticated]);

  const checkAuth = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/verify`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setIsAuthenticated(true);
        setUserRole(data.role || 'admin');  // Get role from verify response
        setCurrentUser(data.username || 'dashboard');
      }
    } catch (err) {
      console.log('Not authenticated');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) {
      setError('Enter password');
      return;
    }

    setLoading(true);
    try {
      // Build login payload - include username if provided (multi-user mode)
      const loginPayload = { password };
      if (username.trim()) {
        loginPayload.username = username.trim();
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/login`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(loginPayload),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        setError(data.detail || 'Invalid credentials');
        setLoading(false);
        return;
      }

      const data = await response.json();
      if (data.success) {
        setIsAuthenticated(true);
        setUserRole(data.role || 'admin');  // Set role from login response
        setCurrentUser(data.username || 'dashboard');
        setError('');
        setPassword('');
        setUsername('');
      } else {
        setError('Login failed');
      }
    } catch (err) {
      setError('Connection failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/logout`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );
    } catch (err) {
      console.log('Logout error');
    }
    setIsAuthenticated(false);
    setPassword('');
    setUsername('');
    setUserRole('admin');
    setCurrentUser('');
  };

  // Modern SpiralArc Logo Component
  const Logo = ({ size = 40 }) => (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="arcGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00D4FF" />
          <stop offset="50%" stopColor="#00C853" />
          <stop offset="100%" stopColor="#7C3AED" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      {/* Outer arc */}
      <path
        d="M 80 50 A 30 30 0 1 1 50 20"
        stroke="url(#arcGradient)"
        strokeWidth="6"
        strokeLinecap="round"
        fill="none"
        filter="url(#glow)"
      />
      {/* Inner spiral */}
      <path
        d="M 50 35 A 15 15 0 1 1 35 50"
        stroke="url(#arcGradient)"
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
        opacity="0.8"
      />
      {/* Center dot */}
      <circle cx="50" cy="50" r="5" fill="url(#arcGradient)" filter="url(#glow)" />
      {/* Accent line (represents trading direction) */}
      <path
        d="M 52 48 L 65 35"
        stroke="#00C853"
        strokeWidth="3"
        strokeLinecap="round"
        opacity="0.9"
      />
    </svg>
  );

  if (loading && !isAuthenticated) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="login-container">
        <div className="login-bg">
          <div className="grid-overlay"></div>
        </div>

        <div className="login-box">
          <div className="login-header">
            <div className="logo-wrapper">
              <Logo size={60} />
            </div>
            <h1>SpiralArc</h1>
            <p>Trading Terminal</p>
          </div>

          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <div className="input-wrapper">
                <input
                  type="text"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  className="username-input"
                />
              </div>
            </div>
            <div className="form-group">
              <div className="input-wrapper">
                <Lock size={18} className="input-icon" />
                <input
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  autoFocus
                  className="password-input"
                />
              </div>
            </div>

            {error && (
              <div className="error-message">{error}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="login-btn"
            >
              <span>{loading ? 'Authenticating...' : 'Access Terminal'}</span>
              <ArrowRight size={18} />
            </button>
          </form>

          <div className="login-footer">
            <div className="footer-line"></div>
            <span>Secure Trading Dashboard</span>
            <div className="footer-line"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <Logo size={32} />
          <div className="header-brand">
            <span className="brand-name">SpiralArc</span>
          </div>
        </div>
        <div className="header-right">
          <div className="live-indicator">
            <span className="live-dot"></span>
            <span>LIVE</span>
          </div>
          <div className="user-info">
            <span className={`role-badge ${userRole}`}>{userRole.toUpperCase()}</span>
            <span className="username">{currentUser}</span>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={16} />
            <span>Exit</span>
          </button>
        </div>
      </header>

      {/* ALL USERS: session-expiry notice — replaces silently frozen panels */}
      {sessionExpired && (
        <div className="session-expired-banner" role="alert">
          <span>Your session expired — live data stopped updating.</span>
          <button type="button" onClick={handleLogout}>Log in again</button>
        </div>
      )}

      <Dashboard userRole={userRole} currentUser={currentUser} />
    </div>
  );
}

export default App;
